/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import { api } from "@app/data/index";
import { Loading, LemmonModal } from "@app/vue/components/index";
import { useShop, useToast } from "@app/vue/providers/index";
import { Printer } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

interface PrintReceiptModalProps {
  paymentId: number;
  onClose: () => void;
}

export const PrintReceiptModal = defineComponent<PrintReceiptModalProps>({
  name: "PrintReceiptModal",
  props: ["paymentId", "onClose"],
  setup(props) {
    const selectedPrinterId = ref<number | null>(null);
    const loading = ref(true);
    const printing = ref(false);
    const shop = useShop();
    const toast = useToast();
    const printers = ref<Printer[]>([]);
    const { t } = useLocalization();
    onMounted(async () => {
      try {
        const devices = shop.getPrinters();
        if (devices) {
          printers.value = devices;
        }
      } catch (error) {
        console.error("Failed to fetch printers:", error);
        toast.showToast("Failed to load printers", { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const handleSubmit = async () => {
      if (!selectedPrinterId.value) return;

      try {
        printing.value = true;
        const printDoc = await (async function () {
          try {
            const printDoc = await api.getTaxReceipt(props.paymentId);
            return printDoc;
          } catch (error) {
            console.error("Failed to print receipt:", error);
            toast.showToast("Failed to print receipt", {
              type: "warning",
              icon: "print_error",
            });
          }
        })();
        if (printDoc) {
          await shop.print({
            doc: printDoc,
            printerId: selectedPrinterId.value,
          });
          toast.showToast(t("general.success_notification.receipt_printed"), {
            type: "success",
            icon: "print",
            autoHide: 3000,
          });
          props.onClose();
        }
      } catch (error) {
        console.error("Failed to print receipt:", error);
        toast.showToast("Failed to print receipt", {
          type: "warning",
          icon: "print",
        });
      } finally {
        printing.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("reports.tx.print_reciept.title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="p-4 border-t ">
            <div class="flex justify-between gap-2">
              <button onClick={props.onClose} class="btn btn-neutral">
                {t("general.words.cancel")}
              </button>
              <button
                onClick={handleSubmit}
                class="btn btn-primary"
                disabled={!selectedPrinterId.value || printing.value}
              >
                {printing.value ? (
                  <span class="flex items-center justify-center">
                    <span class="loading loading-spinner loading mr-2"></span>
                    {t("general.words.print")}...
                  </span>
                ) : (
                  <span class="flex items-center">
                    <span class="material-symbols-outlined mr-2">print</span>
                    {t("general.words.print")}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
      >
        <div class="p-6">
          {loading.value ? (
            <div class="flex justify-center">
              <Loading />
            </div>
          ) : (
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium  mb-2">
                  {t("reports.tx.print_reciept.select_printer")}
                </label>
                <select
                  class="select select-bordered w-full bg-gray-900 text-accent"
                  value={selectedPrinterId.value || ""}
                  onChange={(e) => {
                    selectedPrinterId.value = Number(
                      (e.target as HTMLSelectElement).value,
                    );
                  }}
                >
                  <option value="">
                    {t("reports.tx.print_reciept.select_a_printer")}
                  </option>
                  {printers.value.map((printer) => (
                    <option key={printer.id} value={printer.id}>
                      {printer.name} | {printer.serial_number} | {printer.ip}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </LemmonModal>
    );
  },
});
